import { useCallback, useContext, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { InfoContent } from '../../../components/InfoContent';
import { SubmitModal } from '../../../components/Modal/SubmitModal';
import { UploadProgressModal } from '../../../components/UploadCard/UploadProgressModal';
import { UserContext } from '../../../contexts/UserContext';
import { T, useT } from '../../../translation/src';
import useOpen from '../../useOpen';
import { toast } from 'react-toastify';

const GB150InBytes = 150000000000;

/** The only file formats the point-cloud pipeline can process. */
export const POINT_CLOUD_FILE_EXTENSIONS = ['.las', '.pts', '.laz', '.e57'];
/** Pythagoras project uploads are a single zip archive. */
export const PYTHAGORAS_FILE_EXTENSIONS = ['.zip'];

/** Whether the file's extension is one of `extensions` (case-insensitive).
 *  The `accept` attribute / dropzone MIME filter is only a picker hint —
 *  "All files" and drag-and-drop bypass it, so every upload entry point must
 *  check explicitly. */
export const isSupportedUploadFile = (file: File, extensions: string[]) => {
  const name = file.name.toLowerCase();
  return extensions.some((extension) => name.endsWith(extension));
};

export const useMaxFiles = () => {
  const currentUser = useContext(UserContext);
  const { organisationId = '' } = useParams();
  const organisation = currentUser.organisations.find((org) => org.id === organisationId);
  return organisation?.subscription.simultaneousUploadsLeft || -1;
};

// Credits an upload may draw on — the balance minus uploads already underway.
export const useAvailableCredits = () => {
  const currentUser = useContext(UserContext);
  const { organisationId = '' } = useParams();
  const organisation = currentUser.organisations.find((org) => org.id === organisationId);
  return organisation?.credits.available ?? 0;
};

export const useTranslations = ({ allowedExtensions }: { allowedExtensions: string[] }) => {
  const maxUploadExceeded = useT('file upload of {max}GB exceeded', { swc: true, args: { max: 150 } });
  const unsupportedFileType = useT('unsupported file type, only {types} files can be uploaded', { swc: true, args: { types: allowedExtensions.join(', ') } });
  const insufficientCredits = useT('You have no projects left. Buy projects, or contact sales for a subscription with monthly projects included.');

  return { maxUploadExceeded, unsupportedFileType, insufficientCredits };
};

export const usePointCloudDropWithModals = ({ isPythagorasFile = false }: { isPythagorasFile?: boolean } = {}) => {
  const { open: maxFilesModalOpen, onClose: closeMaxFilesModal, onOpenWithValue: openMaxFilesModal } = useOpen();
  const { open: uploadProgressModalOpen, onClose: onCloseUploadProgressModal, onOpenWithValue: openUploadProgressModal, value: uploadProgressModalFiles } = useOpen<File[]>();
  const maxFiles = useMaxFiles();
  const availableCredits = useAvailableCredits();
  const user = useContext(UserContext);
  const allowedExtensions = isPythagorasFile ? PYTHAGORAS_FILE_EXTENSIONS : POINT_CLOUD_FILE_EXTENSIONS;
  const translations = useTranslations({ allowedExtensions });

  const onNewFiles = useCallback(
    (files: File[]) => {
      if (files.some((file) => !isSupportedUploadFile(file, allowedExtensions))) {
        return toast.error(translations.unsupportedFileType);
      }
      if (files.some((file) => file.size > GB150InBytes)) {
        return toast.error(translations.maxUploadExceeded);
      }
      // Block before anything is uploaded or converted — the server refuses too,
      // but the drop zone is reachable even when the upload button is hidden.
      if (!user.isSuperAdmin && availableCredits < 1) return toast.error(translations.insufficientCredits);
      if (!user.isSuperAdmin && !!files.length && files.length > maxFiles) return openMaxFilesModal();
      return openUploadProgressModal(files);
    },
    [allowedExtensions, availableCredits, maxFiles, openMaxFilesModal, openUploadProgressModal, translations.insufficientCredits, translations.maxUploadExceeded, translations.unsupportedFileType, user.isSuperAdmin],
  );

  const modals = useMemo(() => {
    return (
      <>
        <SubmitModal open={maxFilesModalOpen} onCancel={closeMaxFilesModal} title={<T _str="Too many files" />} key="maxFilesModal">
          <InfoContent
            type="info"
            title={<T _str="Too many files" swc />}
            description={<T _str="There is a restriction on the max amount of files you can simultaneously upload. The amount of files you can upload now is {amount}" args={{ amount: maxFiles }} />}
          />
        </SubmitModal>
        <UploadProgressModal key="uploadprogressmodal" open={uploadProgressModalOpen} files={uploadProgressModalFiles} onClose={onCloseUploadProgressModal} isPythagorasFile={isPythagorasFile} />
      </>
    );
  }, [maxFilesModalOpen, closeMaxFilesModal, maxFiles, uploadProgressModalOpen, uploadProgressModalFiles, onCloseUploadProgressModal, isPythagorasFile]);

  return [{ modals }, { onNewFiles }] as const;
};

export const usePointCloudDrop = ({ isPythagorasFile, onNewFiles }: { isPythagorasFile?: boolean; onNewFiles?: (files: File[]) => void } = {}) => {
  const [files, setFiles] = useState<File[] | undefined>();
  const allowedExtensions = isPythagorasFile ? PYTHAGORAS_FILE_EXTENSIONS : POINT_CLOUD_FILE_EXTENSIONS;
  const translations = useTranslations({ allowedExtensions });
  const onDrop = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      onNewFiles?.(files);
      setFiles(files);
    },
    [onNewFiles],
  );
  // The dropzone silently filters files its `accept` config rejects — they
  // never reach onDrop, so without this the picker/drop just does nothing.
  // The only rejection reason configured is the type filter, so the
  // unsupported-type toast is always the right message.
  const onDropRejected = useCallback(() => {
    toast.error(translations.unsupportedFileType);
  }, [translations.unsupportedFileType]);
  const maxFiles = useMaxFiles();

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: true,
    accept: isPythagorasFile
      ? {
          'application/octet-stream': ['.zip'],
        }
      : {
          'application/vnd.las': ['.las'],
          'application/octet-stream': ['.laz', '.pts'],
          'application/vnd.e57': ['.e57'],
        },
  });
  return [
    { files, maxFiles },
    { setFiles, getRootProps, getInputProps },
  ] as const;
};
